// src/components/events/EventSlider.js

import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { FaChevronLeft, FaChevronRight, FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
} from "firebase/storage";

export default function EventSlider() {
  const swiperRef = useRef();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);

  // 날짜 파싱 헬퍼
  const parseStart = (str) => {
    if (typeof str !== "string") return null;
    const d = new Date(str.replace(/\./g, "-") + "T00:00:00");
    return isNaN(d.getTime()) ? null : d;
  };
  const parseEnd = (str) => {
    if (typeof str !== "string") return null;
    const d = new Date(str.replace(/\./g, "-") + "T23:59:59");
    return isNaN(d.getTime()) ? null : d;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      // 1) bannerList 문서에서 event 배열 가져오기
      const bannerListDoc = doc(db, "data", "bannerList");
      const bannerSnap = await getDoc(bannerListDoc);
      const ids = bannerSnap.exists() ? bannerSnap.data().event || [] : [];

      // 2) events 컬렉션에서 모든 문서 가져오기
      const snap = await getDocs(collection(db, "events"));
      const storage = getStorage();

      // 3) bannerIds 배열에 포함된 ID만 처리
      const docs = await Promise.all(
        snap.docs
          .filter((d) => ids.includes(d.id))
          .map(async (d) => {
            const data = d.data();
            const cacheKey = `eventBanner_${d.id}`;
            // localStorage 캐시 확인
            let imageUrl = localStorage.getItem(cacheKey) || "";

            if (!imageUrl && data.bannerImageUrl) {
              if (data.bannerImageUrl.startsWith("http")) {
                imageUrl = data.bannerImageUrl;
              } else {
                try {
                  imageUrl = await getDownloadURL(
                    storageRef(storage, data.bannerImageUrl)
                  );
                } catch {
                  imageUrl = "";
                }
              }
              if (imageUrl) {
                try {
                  localStorage.setItem(cacheKey, imageUrl);
                } catch {}
              }
            }

            return {
              id: d.id,
              image: imageUrl,
              title: data.title,
              subtitle: data.subtitle,
              startDate: data.startDate,
              endDate: data.endDate,
              views: data.viewCount || 0,
            };
          })
      );

      setEvents(docs);
    };

    fetchEvents();
  }, []);

  const handlePrev = () => swiperRef.current?.slidePrev();
  const handleNext = () => swiperRef.current?.slideNext();

  const today = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;

  return (
    <div className="w-full flex flex-col items-center pt-12 pb-6">
      <div className="w-full container mx-auto px-6">
        <Swiper
          modules={[Pagination]}
          slidesPerView={4}
          slidesPerGroup={1}
          spaceBetween={24}
          onSwiper={(s) => (swiperRef.current = s)}
          pagination={{
            el: ".event-pagination",
            clickable: true,
            renderBullet: (i, className) =>
              `<span class="${className} w-2 h-2 inline-block mx-1 bg-gray-200 rounded-full transition-all duration-300"></span>`,
          }}
          breakpoints={{
            0: { slidesPerView: 1 },
            640: { slidesPerView: 2 },
            1024: { slidesPerView: 4 },
          }}
          className="w-full"
        >
          {events.map((ev) => {
            const sd = parseStart(ev.startDate);
            const ed = parseEnd(ev.endDate);
            let status = "";
            let daysLeft = 0;

            if (sd && ed) {
              if (today < sd) {
                status = "진행예정";
                daysLeft = Math.floor((sd - today) / msPerDay);
              } else if (today > ed) {
                status = "종료";
              } else {
                status = "진행중";
                daysLeft = Math.floor((ed - today) / msPerDay);
              }
            }

            let extraBadge = null;
            if (status === "진행중") {
              if (daysLeft >= 1 && daysLeft <= 3) {
                extraBadge = `D-${daysLeft}`;
              } else if (daysLeft === 0) {
                extraBadge = "마감임박";
              }
            }

            const statusColor =
              status === "진행중"
                ? "bg-green-600"
                : status === "진행예정"
                ? "bg-yellow-600"
                : "bg-gray-600";

            return (
              <SwiperSlide key={ev.id}>
                <div className="flex justify-center">
                  <div
                    className="relative group bg-[#121212] rounded-2xl overflow-hidden
                                shadow-lg hover:shadow-xl transition flex flex-col w-full
                                max-w-[340px] cursor-pointer border-2 border-neutral-600"
                    onClick={() => navigate(`/events/detail/${ev.id}`)}
                  >
                    {/* 상태 배지 + D-Day */}
                    <div className="absolute top-3 left-3 flex space-x-2 z-10">
                      {status && (
                        <span
                          className={`px-2 py-1 rounded-full text-sm font-semibold text-white ${statusColor}`}
                        >
                          {status}
                        </span>
                      )}
                      {extraBadge && (
                        <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          {extraBadge}
                        </span>
                      )}
                    </div>

                    {/* 16:9 비율 이미지 */}
                    <div className="w-full aspect-img bg-black overflow-hidden flex items-center justify-center">
                      {ev.image ? (
                        <img
                          src={ev.image}
                          alt={ev.title}
                          className="object-contain w-full h-full"
                          draggable={false}
                        />
                      ) : (
                        <span className="text-gray-500">이미지 없음</span>
                      )}
                    </div>

                    {/* 콘텐츠 */}
                    <div className="p-4 flex flex-col justify-between bg-neutral-800 flex-1">
                      <h3 className="text-xl font-bold text-white truncate pb-6">
                        {ev.title}
                      </h3>
                      <div className="flex justify-between items-center text-gray-400 text-sm font-medium">
                        <span className="truncate">
                          {ev.startDate} ~ {ev.endDate}
                        </span>
                        <span className="flex items-center">
                          <FaEye className="mr-2" />
                          {ev.views}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          className="bg-neutral-700/70 text-white rounded-full p-2 hover:bg-neutral-500 transition"
          onClick={handlePrev}
        >
          <FaChevronLeft size={18} />
        </button>
        <div className="event-pagination flex items-center justify-center gap-2" />
        <button
          className="bg-neutral-700/70 text-white rounded-full p-2 hover:bg-neutral-500 transition"
          onClick={handleNext}
        >
          <FaChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}
